<template>
	<section id="advantages" class="advantages">
		<div class="advantages__container container">
			<h2 class="advantages__title heading h2">
				{{ $t('pages.main.sections.advantages.title') }}
			</h2>
			<div class="advantages__header">
				<div class="advantages__header-title">VEON Ads</div>
				<UIHeading level="3" class="advantages__header-subtitle">
					{{ $t('pages.main.sections.advantages.subtitle') }}
				</UIHeading>
				<p class="advantages__header-text">
					{{ $t('pages.main.sections.advantages.text') }}
				</p>
			</div>
			<div class="advantages__content">
				<!-- Canvas for image sequence - только на десктопе (скрыт через CSS на мобилке) -->
				<div ref="mediaRef" class="advantages__media">
					<canvas ref="canvasRef" class="advantages__canvas advantages__canvas--desktop"></canvas>

					<!-- Video - только на мобилке (скрыт через CSS на десктопе) -->
					<video
						v-if="isMounted && isMobile"
						id="video-adv"
						ref="videoRef"
						src="https://dcdn.veonadx.com/landingveon/output-adv2.mp4"
						muted
						playsinline
						autoplay
						loop
						preload="metadata"
						poster="/images/freq/adv/adv_0001.webp"
						class="advantages__video advantages__video--mobile">
						<track kind="captions" srclang="en" label="English captions" src="/captions/empty.vtt" />
					</video>
				</div>

				<BlocksTimeline :data="advantages" />
			</div>
			<!-- <BlocksMobileTimeline v-if="isMobile" :data="advantages" /> -->
		</div>
	</section>
</template>

<script setup lang="ts">
	const { t } = useI18n()
	const { $gsap: gsap } = useNuxtApp()
	const FRAME_COUNT = 360
	const BASE_FPS = 48
	const INITIAL_PRELOAD_COUNT = 18
	const PRELOAD_RANGE = 8
	const MAX_SCROLL_BOOST = 5
	const MOUSE_SHIFT_PX = 28
	const SCALE_INTENSITY = 0.07

	const advantages = [
		{
			id: 'own-media-inventory',
			title: t(`pages.main.sections.advantages.item1.title`),
			text: t(`pages.main.sections.advantages.item1.text`),
		},
		{
			id: 'target-audience',
			title: t(`pages.main.sections.advantages.item2.title`),
			text: t(`pages.main.sections.advantages.item2.text`),
		},
		{
			id: 'quality-traffic',
			title: t(`pages.main.sections.advantages.item3.title`),
			text: t(`pages.main.sections.advantages.item3.text`),
		},
		{
			id: 'transparency-and-control',
			title: t(`pages.main.sections.advantages.item4.title`),
			text: t(`pages.main.sections.advantages.item4.text`),
		},
		{
			id: 'precision-cjm',
			title: t(`pages.main.sections.advantages.item5.title`),
			text: t(`pages.main.sections.advantages.item5.text`),
		},
		{
			id: 'optimization-and-conversion',
			title: t(`pages.main.sections.advantages.item6.title`),
			text: t(`pages.main.sections.advantages.item6.text`),
		},
		{
			id: 'personalization-for-segments',
			title: t(`pages.main.sections.advantages.item7.title`),
			text: t(`pages.main.sections.advantages.item7.text`),
		},
		{
			id: 'romi-growth',
			title: t(`pages.main.sections.advantages.item8.title`),
			text: t(`pages.main.sections.advantages.item8.text`),
		},
		{
			id: 'automated-adaptation',
			title: t(`pages.main.sections.advantages.item9.title`),
			text: t(`pages.main.sections.advantages.item9.text`),
		},
	]

	const isMobile = ref(false)
	const isMounted = ref(false)
	const videoRef = ref<HTMLVideoElement | null>(null)
	const mediaRef = ref<HTMLDivElement | null>(null)

	// Image sequence config using composable
	const {
		canvasRef,
		images,
		currentFrame,
		init: initImageSequence,
		drawFrame,
		preloadFrames,
	} = useImageSequence({
		frameCount: FRAME_COUNT,
		getFramePath: (index: number) => {
			const frameNumber = String(index + 1).padStart(4, '0')
			return `/images/freq/adv/adv_${frameNumber}.jpg`
		},
		triggerSelector: '.advantages__content',
		triggerStart: 'top bottom',
		triggerEnd: 'bottom top',
		initialPreloadCount: INITIAL_PRELOAD_COUNT,
		preloadRange: PRELOAD_RANGE,
	})

	let autoplayTicker: ((time: number, deltaTime: number, frame: number) => void) | null = null
	let scrollControlTween: gsap.core.Tween | null = null
	let frameFloat = 0
	let currentPlaybackSpeed = 1
	let targetPlaybackSpeed = 1
	let lastScrollDirection = 1
	let cleanupMouseParallax: (() => void) | null = null

	function updateTargetPlaybackSpeed(nextSpeed: number) {
		targetPlaybackSpeed = gsap.utils.clamp(-MAX_SCROLL_BOOST, MAX_SCROLL_BOOST, nextSpeed)
	}

	function startCanvasAutoplay() {
		if (autoplayTicker) return

		autoplayTicker = (_time: number, deltaTime: number) => {
			// Smooth speed transition so scroll only "nudges" playback.
			currentPlaybackSpeed += (targetPlaybackSpeed - currentPlaybackSpeed) * 0.12

			const frameStep = (deltaTime / 1000) * BASE_FPS * currentPlaybackSpeed
			frameFloat = (frameFloat + frameStep + FRAME_COUNT) % FRAME_COUNT

			const frameIndex = Math.floor(frameFloat)
			const frameImage = images.value[frameIndex]
			if (!frameImage) return

			if (!frameImage.src) {
				frameImage.src = `/images/freq/adv/adv_${String(frameIndex + 1).padStart(4, '0')}.jpg`
			}

			if (frameImage.complete && frameImage.naturalWidth > 0) {
				drawFrame(frameIndex)
				currentFrame.value = frameIndex
				void preloadFrames(frameIndex - PRELOAD_RANGE, frameIndex + PRELOAD_RANGE)
			}
		}

		gsap.ticker.add(autoplayTicker)
	}

	function setupScrollSpeedControl() {
		scrollControlTween?.kill()

		scrollControlTween = gsap.to({}, {
			scrollTrigger: {
				trigger: '.advantages__content',
				start: 'top bottom',
				end: 'bottom top',
				onUpdate: (self: { getVelocity: () => number; direction: number }) => {
					const velocity = Math.abs(self.getVelocity())
					if (velocity > 10) {
						lastScrollDirection = self.direction
					}
					const boost = gsap.utils.clamp(0, MAX_SCROLL_BOOST - 1, velocity / 600)
					updateTargetPlaybackSpeed(lastScrollDirection * (1 + boost))
				},
			},
		})
	}

	function setupDesktopMouseParallax() {
		const mediaElement = mediaRef.value
		const parallaxTarget = canvasRef.value ?? mediaElement
		if (!parallaxTarget) return

		const moveX = gsap.quickTo(parallaxTarget, 'x', { duration: 0.45, ease: 'power3.out' })
		const moveY = gsap.quickTo(parallaxTarget, 'y', { duration: 0.45, ease: 'power3.out' })
		const scaleXTo = gsap.quickTo(parallaxTarget, 'scaleX', {
			duration: 0.45,
			ease: 'power3.out',
		})
		const scaleYTo = gsap.quickTo(parallaxTarget, 'scaleY', {
			duration: 0.45,
			ease: 'power3.out',
		})

		const handleMouseMove = (event: MouseEvent) => {
			const viewportWidth = window.innerWidth || 1
			const viewportHeight = window.innerHeight || 1
			const normalizedX = event.clientX / viewportWidth - 0.5
			const normalizedY = event.clientY / viewportHeight - 0.5
			const yRatio = event.clientY / viewportHeight
			const halfDistance = Math.abs(0.5 - yRatio) * 2
			const targetScaleRaw =
				yRatio <= 0.5 ? 1 + halfDistance * SCALE_INTENSITY : 1 - halfDistance * SCALE_INTENSITY
			const targetScale = gsap.utils.clamp(0.35, 1.85, targetScaleRaw)
			moveX(normalizedX * MOUSE_SHIFT_PX)
			moveY(normalizedY * MOUSE_SHIFT_PX)
			scaleXTo(targetScale)
			scaleYTo(targetScale)
		}

		const handleMouseLeave = () => {
			moveX(0)
			moveY(0)
			scaleXTo(1)
			scaleYTo(1)
		}

		window.addEventListener('mousemove', handleMouseMove)
		window.addEventListener('mouseleave', handleMouseLeave)

		cleanupMouseParallax = () => {
			window.removeEventListener('mousemove', handleMouseMove)
			window.removeEventListener('mouseleave', handleMouseLeave)
			moveX(0)
			moveY(0)
			scaleXTo(1)
			scaleYTo(1)
		}
	}

	// Анимация видео на мобилке
	function videoAnimationOnScroll() {
		gsap
			.timeline()
			.to(videoRef.value, {
				scrollTrigger: {
					trigger: '.advantages__content',
					start: 'top top',
					end: 'bottom bottom',
					scrub: true,
				},
				ease: 'none',
				xPercent: 50,
			})
			.to(videoRef.value, {
				scrollTrigger: {
					trigger: '.advantages__media',
					start: 'top top',
					end: 'bottom top',
					scrub: true,
				},
				ease: 'none',
				opacity: 0.1,
			})
	}

	onMounted(async () => {
		// Определяем мобилку на клиенте
		isMobile.value = window.innerWidth < 992
		isMounted.value = true

		// Canvas только на десктопе
		if (!isMobile.value) {
			await initImageSequence()
			setTimeout(() => {
				startCanvasAutoplay()
				setupScrollSpeedControl()
				setupDesktopMouseParallax()
			}, 100)
		} else {
			// Запускаем видео на мобилке
			await nextTick()
			if (videoRef.value) {
				videoRef.value.play().catch((e) => console.warn('Autoplay failed:', e))
			}
			setTimeout(() => {
				videoAnimationOnScroll()
			}, 100)
		}
	})

	onBeforeUnmount(() => {
		if (autoplayTicker) {
			gsap.ticker.remove(autoplayTicker)
			autoplayTicker = null
		}
		scrollControlTween?.kill()
		cleanupMouseParallax?.()
	})

	// Video ready hook commented out - using image sequence instead
	/*
	useVideoReady(
		'video-adv',
		() => {
			setTimeout(() => {
				advantagesAnimation()
			}, 500)
		},
		videoRef
	)
	*/
</script>

<style lang="scss" scoped>
	.advantages {
		// scroll-margin-top: 80px;
		position: relative;
		top: -50px;
		z-index: 1;
		padding: 50px 0 100px;
		background-color: var(--white);
		color: var(--black);
		border-radius: 50px 50px 0 0;

		@media (width <= 768px) {
			overflow: clip;
			border-radius: 20px;

			// overflow-x: clip;
			// overflow-y: overlay;
		}
		&__title {
			text-transform: uppercase;

			@media (width <=991.98px) {
				text-align: center;
			}
		}
		&__header {
			position: relative;
			z-index: 1;
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			text-align: center;
			&-title {
				font-family: Montserrat, sans-serif;
				font-size: clamp(4rem, 5vw, 6rem);
				font-weight: 700;
				color: #7a5c00;
				letter-spacing: -6px;
			}
			&-subtitle {
				max-width: 40%;

				// text-transform: uppercase;
				font-family: Montserrat, sans-serif;

				@media (width <=991.98px) {
					max-width: 100%;
				}
			}
			&-text {
				max-width: 60%;
				margin-top: 32px;

				@media (width <=991.98px) {
					max-width: 100%;
				}
			}
		}
		&__content {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 64px;
			min-width: 0;
			margin-top: 42px;
			text-align: center;

			@media (width <= 991.98px) {
				display: flex;
				flex-direction: column;
			}
		}
		&__canvas {
			position: sticky;
			top: 80px;
			z-index: -1;
			width: 100%;
			max-height: 400px;
			object-fit: contain;
			transform-origin: top;
			will-change: transform;
			&--desktop {
				display: block;

				@media (width <= 992px) {
					display: none;
				}
			}
		}
		&__media {
			position: sticky;
			top: 80px;
			z-index: -1;
			width: 100%;
			max-height: 400px;
			object-fit: contain;
			transform-origin: top;
			will-change: transform;
		}
		&__video {
			width: 100%;
			height: 100%;
			max-height: 400px;
			&--mobile {
				display: none;

				@media (width <= 992px) {
					display: block;
					transform: translateX(-50%);
				}
			}
		}
	}
</style>
